/**
 * Auth method resolution — pure, verbatim from source
 * (defaultMethods / supportedMethods / recommendedMethod).
 */
import type { AuthMethod, Integration } from './types';

export type AuthCfg = {
  methods?: AuthMethod[];
  recommended?: AuthMethod;
  headerName?: string;
  keyValue?: string;
  saved?: boolean;
  method?: AuthMethod;
};

/** Method labels (toggle copy + toasts). */
export const METHOD_LABEL: Record<AuthMethod, string> = {
  oauth: 'OAuth2',
  apikey: 'API key',
  bearer: 'Bearer',
};

/** Auth panel meta: [label, note] per method. */
export const METHOD_META: Record<AuthMethod, [string, string]> = {
  oauth: ['OAuth2', 'Tenant authorizes via redirect — no secrets shared.'],
  apikey: ['API key', 'Tenant pastes a key sent in a header.'],
  bearer: ['Bearer token', 'Tenant pastes a bearer token.'],
};

const ORDER: AuthMethod[] = ['oauth', 'apikey', 'bearer'];

/** Default methods by adapter class. */
export function defaultMethods(int: Integration | undefined): AuthMethod[] {
  if (int && int.cls === 'mcp') return ['oauth'];
  if (int && int.cls === 'openapi') return ['oauth', 'apikey'];
  return ['oauth'];
}

/** Enabled methods: explicit cfg override, else class defaults. */
export function enabledMethods(cfg: AuthCfg | undefined, int: Integration | undefined): AuthMethod[] {
  if (cfg && cfg.methods) return cfg.methods;
  return defaultMethods(int);
}

/** Recommended method: cfg.recommended if still enabled, else first enabled. */
export function recommendedMethod(cfg: AuthCfg | undefined, int: Integration | undefined): AuthMethod {
  const enabled = enabledMethods(cfg, int);
  if (cfg && cfg.recommended && enabled.includes(cfg.recommended)) return cfg.recommended;
  return enabled[0] || 'oauth';
}

/** Sort a method list into canonical order (oauth, apikey, bearer). */
export function sortMethods(list: AuthMethod[]): AuthMethod[] {
  return [...list].sort((a, b) => ORDER.indexOf(a) - ORDER.indexOf(b));
}
